import { apiGet, apiPost } from './apiClient';

export interface DepositaryCashMonitor {
  id: string;
  fund_id: string;
  fund_name: string;
  account_name: string;
  currency: string;
  amount: number;
  movement_type: string;
  counterparty: string;
  value_date: string;
  status: string;
  flagged_reason: string | null;
  confirmed_by: string | null;
  confirmed_at: string | null;
  created_at: string;
}

export interface DepositarySafekeeping {
  id: string;
  fund_id: string;
  fund_name: string;
  asset_name: string;
  isin: string;
  quantity_fund: number;
  quantity_custodian: number;
  difference: number;
  status: string;
  reconciliation_date: string;
  resolved_by: string | null;
  resolved_at: string | null;
  resolution_notes: string | null;
  created_at: string;
}

export interface DepositaryNavVerification {
  id: string;
  fund_id: string;
  fund_name: string;
  nav_date: string;
  fund_nav: number;
  depositary_nav: number | null;
  difference_pct: number | null;
  status: string;
  verified_by: string | null;
  verified_at: string | null;
  dispute_reason: string | null;
  created_at: string;
}

export interface DepositaryReport {
  id: string;
  fund_id: string;
  fund_name: string;
  report_type: string;
  report_period: string;
  status: string;
  file_path: string | null;
  generated_at: string;
  approved_by: string | null;
  approved_at: string | null;
  submitted_at: string | null;
  created_at: string;
}

export const depositaryService = {
  recordCash: (data: Omit<DepositaryCashMonitor, 'id' | 'status' | 'flagged_reason' | 'confirmed_by' | 'confirmed_at' | 'created_at' | 'fund_name'>) =>
    apiPost<DepositaryCashMonitor>('/depositary/cash', data),

  listCash: async (fundId?: string, status?: string) => {
    const res = await apiGet<{ items: DepositaryCashMonitor[] }>('/depositary/cash', {
      params: { fund_id: fundId, status },
    });
    return res.items || [];
  },

  confirmCash: (id: string) =>
    apiPost<DepositaryCashMonitor>(`/depositary/cash/${id}/confirm`),

  flagCash: (id: string, reason: string) =>
    apiPost<DepositaryCashMonitor>(`/depositary/cash/${id}/flag`, { reason }),

  triggerReconciliation: (fundId: string, date: string) =>
    apiPost<DepositarySafekeeping[]>('/depositary/safekeeping/reconcile', {
      fund_id: fundId,
      date,
    }),

  listSafekeeping: async (fundId?: string, status?: string) => {
    const res = await apiGet<{ items: DepositarySafekeeping[] }>('/depositary/safekeeping', {
      params: { fund_id: fundId, status },
    });
    return res.items || [];
  },

  resolveBreak: (id: string, notes: string) =>
    apiPost<DepositarySafekeeping>(`/depositary/safekeeping/${id}/resolve`, { notes }),

  submitNavVerification: (data: { fund_id: string; nav_date: string; fund_nav: number }) =>
    apiPost<DepositaryNavVerification>('/depositary/nav-verification', data),

  listNavVerifications: async (fundId?: string, status?: string) => {
    const res = await apiGet<{ items: DepositaryNavVerification[] }>('/depositary/nav-verification', {
      params: { fund_id: fundId, status },
    });
    return res.items || [];
  },

  verifyNav: (id: string, depositaryNav: number) =>
    apiPost<DepositaryNavVerification>(`/depositary/nav-verification/${id}/verify`, {
      depositary_nav: depositaryNav,
    }),

  disputeNav: (id: string, reason: string) =>
    apiPost<DepositaryNavVerification>(`/depositary/nav-verification/${id}/dispute`, { reason }),

  generateReport: (data: { fund_id: string; report_type: string; report_period: string }) =>
    apiPost<DepositaryReport>('/depositary/reports/generate', data),

  listReports: async (fundId?: string, status?: string) => {
    const res = await apiGet<{ items: DepositaryReport[] }>('/depositary/reports', {
      params: { fund_id: fundId, status },
    });
    return res.items || [];
  },

  getReport: (id: string) =>
    apiGet<DepositaryReport>(`/depositary/reports/${id}`),

  approveReport: (id: string) =>
    apiPost<DepositaryReport>(`/depositary/reports/${id}/approve`),

  submitReport: (id: string) =>
    apiPost<DepositaryReport>(`/depositary/reports/${id}/submit`),
};
